import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { productSearchApi, StoreResponse, StoreResponses } from '@/stores/api';
import { PageRequestDto, ProductSearchRequestDto, ProductSearchResultDto } from '@/api';
import { ProductSearchResult, ProductTuple, SearchUtility } from '@/stores/search/util';
import * as _ from 'lodash';
import ErrorKey from '@/util/ErrorKey';

@Module({
  namespaced: true,
  name: 'SearchResultStore',
})
export default class SearchResultStore extends VuexModule {
  private searchResult = _.cloneDeep(SearchUtility.EmptySearchResult);
  private searchRequest = _.cloneDeep(SearchUtility.EmptySearchRequest);
  private distinctProductCount = 0;

  get currentSearchRequest(): ProductSearchRequestDto {
    return this.searchRequest;
  }

  get currentProducts(): ProductTuple[] {
    return this.searchResult.products;
  }

  get currentSearchResult(): ProductSearchResult {
    return this.searchResult;
  }

  get currentDistinctProductCount(): number {
    return this.distinctProductCount;
  }

  @Action
  public async updateSearchRequest(searchRequest: ProductSearchRequestDto) {
    this.context.commit('setNewSearchRequest', searchRequest);
    await this.fetchProducts();
  }

  @Action
  public async updateQueryString(queryString: string) {
    const searchRequest: ProductSearchRequestDto = this.currentSearchRequest;
    searchRequest.queryString = queryString;
    this.context.commit('setNewSearchRequest', searchRequest);
    await this.fetchProducts();
  }

  @Action
  public async updateSorting(
    sort: { fieldName?: string, direction?: string },
  ): Promise<StoreResponse<ProductSearchResultDto>> {
    const searchRequest: ProductSearchRequestDto = this.currentSearchRequest;
    if (!searchRequest.page) {
      searchRequest.page = {};
    }
    searchRequest.page.sortBy = sort.fieldName;
    searchRequest.page.sortDirection = sort.direction;
    this.context.commit('setNewSearchRequest', searchRequest);
    return this.fetchProducts();
  }

  @Action
  public async fetchAdditionalPage(pageRequest: PageRequestDto): Promise<StoreResponse<ProductSearchResultDto>> {
    try {
      this.context.commit('setSearchRequestPage', pageRequest);
      const response = await productSearchApi.searchProducts('GRM', this.searchRequest);
      this.context.commit('mergeSearchResults', response.data);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.ProductListFetchAction);
    }
  }

  @Action
  public async fetchProducts(): Promise<StoreResponse<ProductSearchResultDto>> {
    try {
      const response = await productSearchApi.searchProducts('GRM', this.searchRequest);
      this.context.commit('setSearchResult', response.data);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.ProductListFetchAction);
    }
  }

  @Action
  public resetSearchResults() {
    this.context.commit('setSearchResult', SearchUtility.EmptySearchResult);
    this.context.commit('setNewSearchRequest', SearchUtility.EmptySearchRequest);
  }

  @Mutation
  private mergeSearchResults(searchResult: ProductSearchResultDto) {
    this.searchResult.page = searchResult.page;
    this.searchResult.products = this.searchResult.products
      .concat(SearchUtility.splitByUnit(searchResult.products, searchResult.filter));
    this.distinctProductCount += searchResult.products.length;
  }

  @Mutation
  private setSearchResult(searchResult: ProductSearchResultDto) {
    this.searchResult.facets = searchResult.facets;
    this.searchResult.filter = searchResult.filter;
    this.searchResult.page = searchResult.page;
    this.searchResult.queryDurationMs = searchResult.queryDurationMs;
    this.searchResult.products = SearchUtility.splitByUnit(searchResult.products, searchResult.filter);
    this.distinctProductCount = searchResult.products.length;
  }


  @Mutation
  private setNewSearchRequest(searchRequest: ProductSearchRequestDto) {
    if (searchRequest.page) {
      searchRequest.page.page = 0;
    }
    this.searchRequest = searchRequest;
  }

  @Mutation
  private setSearchRequestPage(newPage: PageRequestDto) {
    this.searchRequest.page = newPage;
  }
}
